import type { LocatorJson } from "@msb/reader-bridge";

/**
 * Passages cités d'une réponse du chatbot : le texte cité tel qu'il est écrit dans la réponse, et où il
 * se trouve dans le livre (`metadata.quotes` des messages de l'API). Repris de Studio
 * (dépôt ai, `nextjs/src/components/studio/quoteLinks.ts`).
 */
export interface QuoteSource {
  llm_quote?: string | null;
  locator?: LocatorJson | null;
  /** null : une citation ; "recipe" ou "place" : le nom d'une recette ou d'un lieu, relié à son entrée. */
  kind?: string | null;
}

export const QUOTE_HREF = /^#quote-(\d+)$/;

/**
 * Transforme chaque texte cité d'une réponse en lien markdown `[texte](#quote-N)`.
 *
 * `llm_quote` est le texte entre « » exactement comme dans la réponse (markdown compris) : on le
 * retrouve tel quel. Un texte cité deux fois est relié une fois ; les citations qui se chevauchent
 * sont ignorées. `targets[N]` est le Locator de `#quote-N`.
 */
export function linkQuotes(content: string, quotes: QuoteSource[] | undefined | null): { markdown: string; targets: LocatorJson[] } {
  const targets: LocatorJson[] = [];
  if (!quotes?.length) return { markdown: content, targets };

  const spans: Array<{ start: number; end: number; index: number }> = [];
  const seen = new Set<string>();
  for (const quote of quotes) {
    const text = quote.llm_quote;
    if (!text || !quote.locator?.href || seen.has(text)) continue;
    seen.add(text);
    const start = content.indexOf(text);
    if (start < 0) continue;
    const end = start + text.length;
    if (spans.some((span) => start < span.end && end > span.start)) continue;
    spans.push({ start, end, index: targets.length });
    targets.push(quote.locator);
  }

  // Depuis la fin, pour que les positions des citations précédentes restent justes.
  let markdown = content;
  for (const { start, end, index } of spans.sort((a, b) => b.start - a.start)) {
    const label = content.slice(start, end).replace(/[[\]]/g, "\\$&");
    let before = markdown.slice(0, start);
    let after = markdown.slice(end);
    if (/\n[ \t]*\n/.test(label)) {
      // L'italique ou le gras autour d'une citation de plusieurs paragraphes ne peut pas s'afficher
      // (l'emphase s'arrête au changement de paragraphe) : les astérisques resteraient visibles. On les retire.
      const opening = /(\*{1,3}|_{1,2})(\s*«\s*)$/.exec(before);
      const closing = /^(\s*»)(\*{1,3}|_{1,2})/.exec(after);
      if (opening && closing && opening[1] === closing[2]) {
        before = before.slice(0, before.length - opening[0].length) + opening[2];
        after = closing[1] + after.slice(closing[0].length);
      }
    }
    markdown = `${before}${asLinks(label, index)}${after}`;
  }
  return { markdown, targets };
}

/**
 * `[texte](#quote-N)`. Un lien markdown ne passe pas un changement de paragraphe : une citation de
 * plusieurs paragraphes devient un lien par paragraphe, tous vers le même endroit.
 */
function asLinks(label: string, index: number): string {
  return label
    .split(/(\n[ \t]*\n\s*)/)
    .map((part) => (part.trim() ? `[${part}](#quote-${index})` : part))
    .join("");
}
